import { makeAuthenticatedRequest } from './authService';

export interface StockCompany {
  id: number;
  stock_code: string;
  company_name: string;
  industry?: string;
  sector?: string;
  sub_industry?: string;
  strategy?: 'dividend' | 'growth';
  created_at: string;
  updated_at: string;
}

export interface StockTransaction {
  id: number;
  user_id: number;
  stock_code: string;
  company_name?: string;
  transaction_type: 'buy' | 'sell';
  quantity: number;
  price_per_share: number;
  transaction_date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface StockPrice {
  id: number;
  stock_code: string;
  price: number;
  price_date: string;
  created_at: string;
  updated_at: string;
}

export interface StockHolding {
  stock_code: string;
  company_name?: string;
  industry?: string;
  strategy?: 'dividend' | 'growth';
  total_quantity: number;
  total_cost: number;
  average_cost: number;
  current_price?: number;
  price_date?: string;
  current_value: number;
  gain_loss: number;
  gain_loss_percent: number;
  cash_dividends?: number;
  total_return?: number;
  total_return_percent?: number;
}

export interface StockDividend {
  id: number;
  user_id: number;
  stock_code: string;
  company_name?: string;
  dividend_type: 'cash' | 'stock';
  cash_amount?: number;
  total_cash_received?: number;
  stock_ratio?: string;
  shares_received?: number;
  payment_date: string;
  shares_held?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSummary {
  total_stocks: number;
  total_cost: number;
  total_current_value: number;
  unrealized_gain_loss: number;
  unrealized_gain_loss_percent: number;
  realized_gain_loss: number;
  total_cash_dividends_received: number;
  total_return: number;
  total_return_percent: number;
  stocks_without_price: number;
}

export interface CompanyFormData {
  stock_code: string;
  company_name: string;
}

export interface TransactionFormData {
  stock_code: string;
  transaction_type: 'buy' | 'sell';
  quantity: number;
  price_per_share: number;
  transaction_date: string;
  notes?: string;
}

export interface PriceUpdateData {
  stock_code: string;
  price: number;
  price_date: string;
}

export interface DividendFormData {
  stock_code: string;
  dividend_type: 'cash' | 'stock';
  cash_amount?: number;
  total_cash_received?: number;
  stock_ratio?: string;
  shares_received?: number;
  payment_date: string;
  shares_held?: number;
  notes?: string;
}

export interface StockFinancialReport {
  id: number;
  stock_code: string;
  report_type: 'quarterly' | 'yearly';
  fiscal_year: number;
  quarter: number | null;
  period_start_date: string;
  period_end_date: string;
  report_date: string;
  source?: string;
  revenue?: number;
  net_income?: number;
  earnings_per_share?: number;
  total_assets?: number;
  total_liabilities?: number;
  equity?: number;
  operating_cash_flow?: number;
  debt?: number;
  current_assets?: number;
  current_liabilities?: number;
  current_ratio?: number;
  roe?: number;
  roa?: number;
  gross_profit_margin?: number;
  net_profit_margin?: number;
  operating_profit_margin?: number;
  revenue_growth_rate?: number;
  net_income_growth_rate?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FinancialReportFormData {
  stock_code: string;
  report_type: 'quarterly' | 'yearly';
  fiscal_year: number;
  quarter?: number | null;
  period_start_date: string;
  period_end_date: string;
  report_date: string;
  source?: string;
  revenue?: number;
  net_income?: number;
  earnings_per_share?: number;
  total_assets?: number;
  total_liabilities?: number;
  equity?: number;
  operating_cash_flow?: number;
  debt?: number;
  current_assets?: number;
  current_liabilities?: number;
  current_ratio?: number;
  roe?: number;
  roa?: number;
  gross_profit_margin?: number;
  net_profit_margin?: number;
  operating_profit_margin?: number;
  revenue_growth_rate?: number;
  net_income_growth_rate?: number;
  notes?: string;
}

export interface CashBalance {
  id: number;
  user_id: number;
  balance: number;
  created_at: string;
  updated_at: string;
}

export interface CashTransaction {
  id: number;
  user_id: number;
  transaction_type: 'topup' | 'deduct' | 'stock_buy' | 'stock_sell' | 'dividend';
  amount: number;
  balance_after: number;
  transaction_date: string;
  reference_type?: string;
  reference_id?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CashFormData {
  amount: number;
  transaction_date: string;
  notes?: string;
}

class StockPortfolioService {
  // ============ Company Management ============

  async createCompany(companyData: CompanyFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/stock-portfolio/companies', {
        method: 'POST',
        body: JSON.stringify(companyData)
      });
      return response;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  async getCompanies(): Promise<{ success: boolean; companies?: StockCompany[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/stock-portfolio/companies', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  async getCompanyByCode(stockCode: string): Promise<{ success: boolean; company?: StockCompany }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/companies/${stockCode}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  }

  async updateCompany(stockCode: string, data: { strategy?: 'dividend' | 'growth' | null }): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/companies/${stockCode}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  // ============ Transaction Management ============

  async createTransaction(userId: string, transactionData: TransactionFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const data = {
        user_id: userId,
        ...transactionData
      };

      const response = await makeAuthenticatedRequest('/stock-portfolio/transactions', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }

  async getUserTransactions(userId: string, stockCode?: string, limit?: number, offset?: number): Promise<{ success: boolean; transactions?: StockTransaction[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (stockCode) queryParams.append('stock_code', stockCode);
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());

      const queryString = queryParams.toString();
      const url = `/stock-portfolio/transactions/user/${userId}${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  async getTransactionById(transactionId: string): Promise<{ success: boolean; transaction?: StockTransaction }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/transactions/${transactionId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching transaction:', error);
      throw error;
    }
  }

  async deleteTransaction(transactionId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/transactions/${transactionId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw error;
    }
  }

  // ============ Price Management ============

  async updateStockPrice(priceData: PriceUpdateData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/stock-portfolio/prices', {
        method: 'POST',
        body: JSON.stringify(priceData)
      });
      return response;
    } catch (error) {
      console.error('Error updating stock price:', error);
      throw error;
    }
  }

  async getStockPriceHistory(stockCode: string, limit?: number, offset?: number): Promise<{ success: boolean; prices?: StockPrice[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());

      const queryString = queryParams.toString();
      const url = `/stock-portfolio/prices/${stockCode}${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching price history:', error);
      throw error;
    }
  }

  async getLatestPrice(stockCode: string): Promise<{ success: boolean; price?: StockPrice | null; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/prices/${stockCode}/latest`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching latest price:', error);
      throw error;
    }
  }

  // ============ Portfolio Views ============

  async getUserHoldings(userId: string): Promise<{ success: boolean; holdings?: StockHolding[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/holdings/user/${userId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching holdings:', error);
      throw error;
    }
  }

  async getPortfolioValue(userId: string): Promise<{ success: boolean; portfolio?: PortfolioSummary }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/portfolio-value/user/${userId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching portfolio value:', error);
      throw error;
    }
  }

  // ============ Dividend Management ============

  async createDividend(userId: string, dividendData: DividendFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const data = {
        user_id: userId,
        ...dividendData
      };

      const response = await makeAuthenticatedRequest('/stock-portfolio/dividends', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error creating dividend:', error);
      throw error;
    }
  }

  async getUserDividends(userId: string, stockCode?: string, dividendType?: 'cash' | 'stock', limit?: number, offset?: number): Promise<{ success: boolean; dividends?: StockDividend[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (stockCode) queryParams.append('stock_code', stockCode);
      if (dividendType) queryParams.append('dividend_type', dividendType);
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());

      const queryString = queryParams.toString();
      const url = `/stock-portfolio/dividends/user/${userId}${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching dividends:', error);
      throw error;
    }
  }

  async getDividendById(dividendId: string): Promise<{ success: boolean; dividend?: StockDividend }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/dividends/${dividendId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching dividend:', error);
      throw error;
    }
  }

  async deleteDividend(dividendId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/dividends/${dividendId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting dividend:', error);
      throw error;
    }
  }

  // ============ Helper Functions ============

  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined || isNaN(amount) || !isFinite(amount)) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(0);
    }

    const validAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));

    if (isNaN(validAmount) || !isFinite(validAmount)) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(0);
    }

    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(validAmount);
  }

  formatPercentage(value: number): string {
    if (isNaN(value) || !isFinite(value)) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }

  getCurrentDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  validateTransactionData(data: TransactionFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.stock_code || data.stock_code.trim().length === 0) {
      errors.push('Stock code is required');
    }

    if (!data.transaction_type || !['buy', 'sell'].includes(data.transaction_type)) {
      errors.push('Transaction type must be "buy" or "sell"');
    }

    if (!Number.isInteger(data.quantity) || data.quantity <= 0) {
      errors.push('Quantity must be a positive integer');
    }

    if (data.price_per_share <= 0 || isNaN(data.price_per_share) || !isFinite(data.price_per_share)) {
      errors.push('Price per share must be a valid positive number');
    }

    if (!data.transaction_date) {
      errors.push('Transaction date is required');
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (data.transaction_date && !dateRegex.test(data.transaction_date)) {
      errors.push('Transaction date must be in YYYY-MM-DD format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateCompanyData(data: CompanyFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.stock_code || data.stock_code.trim().length === 0) {
      errors.push('Stock code is required');
    }

    if (!data.company_name || data.company_name.trim().length === 0) {
      errors.push('Company name is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validatePriceData(data: PriceUpdateData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.stock_code || data.stock_code.trim().length === 0) {
      errors.push('Stock code is required');
    }

    if (data.price <= 0 || isNaN(data.price) || !isFinite(data.price)) {
      errors.push('Price must be a valid positive number');
    }

    if (!data.price_date) {
      errors.push('Price date is required');
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (data.price_date && !dateRegex.test(data.price_date)) {
      errors.push('Price date must be in YYYY-MM-DD format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateDividendData(data: DividendFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!data.stock_code || data.stock_code.trim().length === 0) {
      errors.push('Stock code is required');
    }

    if (!data.dividend_type || !['cash', 'stock'].includes(data.dividend_type)) {
      errors.push('Dividend type must be "cash" or "stock"');
    }

    // Type-specific validation
    if (data.dividend_type === 'cash') {
      if (!data.total_cash_received || data.total_cash_received <= 0) {
        errors.push('Total cash received is required and must be positive');
      }
    }

    if (data.dividend_type === 'stock') {
      if (!data.shares_received || !Number.isInteger(data.shares_received) || data.shares_received <= 0) {
        errors.push('Shares received must be a positive integer');
      }
    }

    if (data.shares_held !== undefined && (!Number.isInteger(data.shares_held) || data.shares_held <= 0)) {
      errors.push('Shares held must be a positive integer when provided');
    }

    if (!data.payment_date) {
      errors.push('Payment date is required');
    } else if (!dateRegex.test(data.payment_date)) {
      errors.push('Payment date must be in YYYY-MM-DD format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============ Financial Reports Management ============

  async createFinancialReport(reportData: FinancialReportFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/stock-portfolio/financial-reports', {
        method: 'POST',
        body: JSON.stringify(reportData)
      });
      return response;
    } catch (error) {
      console.error('Error creating financial report:', error);
      throw error;
    }
  }

  async getFinancialReports(stockCode: string, reportType?: 'quarterly' | 'yearly'): Promise<{ success: boolean; reports?: StockFinancialReport[]; count?: number }> {
    try {
      let url = `/stock-portfolio/financial-reports/${stockCode}`;
      if (reportType) {
        url += `/${reportType}`;
      }

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching financial reports:', error);
      throw error;
    }
  }

  async getFinancialReportById(reportId: string): Promise<{ success: boolean; report?: StockFinancialReport }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/financial-reports/id/${reportId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching financial report:', error);
      throw error;
    }
  }

  async updateFinancialReport(reportId: string, reportData: Partial<FinancialReportFormData>): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/financial-reports/${reportId}`, {
        method: 'PUT',
        body: JSON.stringify(reportData)
      });
      return response;
    } catch (error) {
      console.error('Error updating financial report:', error);
      throw error;
    }
  }

  async deleteFinancialReport(reportId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/financial-reports/${reportId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting financial report:', error);
      throw error;
    }
  }

  validateFinancialReportData(data: FinancialReportFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!data.stock_code || data.stock_code.trim().length === 0) {
      errors.push('Stock code is required');
    }

    if (!data.report_type || !['quarterly', 'yearly'].includes(data.report_type)) {
      errors.push('Report type must be "quarterly" or "yearly"');
    }

    if (!data.fiscal_year || !Number.isInteger(data.fiscal_year) || data.fiscal_year < 1900 || data.fiscal_year > 2100) {
      errors.push('Fiscal year must be a valid year between 1900 and 2100');
    }

    if (data.report_type === 'quarterly') {
      if (data.quarter === null || data.quarter === undefined) {
        errors.push('Quarter is required for quarterly reports');
      } else if (![1, 2, 3, 4].includes(data.quarter)) {
        errors.push('Quarter must be 1, 2, 3, or 4');
      }
    }

    if (!data.period_start_date) {
      errors.push('Period start date is required');
    } else if (!dateRegex.test(data.period_start_date)) {
      errors.push('Period start date must be in YYYY-MM-DD format');
    }

    if (!data.period_end_date) {
      errors.push('Period end date is required');
    } else if (!dateRegex.test(data.period_end_date)) {
      errors.push('Period end date must be in YYYY-MM-DD format');
    }

    if (!data.report_date) {
      errors.push('Report date is required');
    } else if (!dateRegex.test(data.report_date)) {
      errors.push('Report date must be in YYYY-MM-DD format');
    }

    // Validate that period_end_date is after period_start_date
    if (data.period_start_date && data.period_end_date) {
      const startDate = new Date(data.period_start_date);
      const endDate = new Date(data.period_end_date);
      if (endDate <= startDate) {
        errors.push('Period end date must be after period start date');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============ Cash Management ============

  async getCashBalance(userId: string): Promise<{ success: boolean; balance: number }> {
    try {
      const response = await makeAuthenticatedRequest(`/stock-portfolio/cash/balance/${userId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching cash balance:', error);
      throw error;
    }
  }

  async topupCash(userId: string, cashData: CashFormData): Promise<{ success: boolean; balance: number; message?: string }> {
    try {
      const data = {
        user_id: userId,
        ...cashData
      };
      const response = await makeAuthenticatedRequest('/stock-portfolio/cash/topup', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error topping up cash:', error);
      throw error;
    }
  }

  async deductCash(userId: string, cashData: CashFormData): Promise<{ success: boolean; balance: number; message?: string }> {
    try {
      const data = {
        user_id: userId,
        ...cashData
      };
      const response = await makeAuthenticatedRequest('/stock-portfolio/cash/deduct', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error deducting cash:', error);
      throw error;
    }
  }

  async getCashTransactions(userId: string, limit?: number, offset?: number): Promise<{ success: boolean; transactions?: CashTransaction[] }> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());

      const queryString = queryParams.toString();
      const url = `/stock-portfolio/cash/transactions/${userId}${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching cash transactions:', error);
      throw error;
    }
  }

  validateCashData(data: CashFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!data.amount || data.amount <= 0 || isNaN(data.amount)) {
      errors.push('Amount must be a valid positive number');
    }
    if (!data.transaction_date) {
      errors.push('Transaction date is required');
    } else if (!dateRegex.test(data.transaction_date)) {
      errors.push('Transaction date must be in YYYY-MM-DD format');
    }
    return { isValid: errors.length === 0, errors };
  }
}

export const stockPortfolioService = new StockPortfolioService();

